// Dice Factory game module — v0.2.1 (primary), v0.1.5 (legacy), v0.3 (sandbox)
// Pure TypeScript, no side effects. All randomness via seeded RNG.

import {
  createGameV3, applyActionV3, getPlayerViewV3, describeV3,
  type DFStateV3, type DFActionV3,
} from './dice-factory-v03';

import {
  Ability, TIER0_ABILITIES, TIER1_ABILITIES, TIER2_ABILITIES,
  TIER3_ABILITIES, TIER4_ABILITIES, getAbilityById,
} from './abilityDecks';
import { FactoryCard, DECK1_CARDS, DECK2_CARDS, DECK3_CARDS } from './factoryCards';

// ===== RNG =====

function rngStep(s: number): [number, number] {
  s = (s ^ (s << 13)) >>> 0;
  s = (s ^ (s >>> 17)) >>> 0;
  s = (s ^ (s << 5)) >>> 0;
  return [s, (s >>> 0) / 0x100000000];
}

function rngInt(s: number, min: number, max: number): [number, number] {
  const [next, f] = rngStep(s);
  return [next, min + Math.floor(f * (max - min + 1))];
}

function shuffleWithRng<T>(arr: T[], rng: number): [T[], number] {
  const a = [...arr];
  let s = rng;
  for (let i = a.length - 1; i > 0; i--) {
    let r: number;
    [s, r] = rngInt(s, 0, i);
    [a[i], a[r]] = [a[r], a[i]];
  }
  return [a, s];
}

// ===== TYPES =====

export type DieSize = 4 | 6 | 8 | 10 | 12;

export interface Die {
  id: string;
  size: DieSize;
  value: number;
}

// v0.2.1 player
export interface DFPlayerV2 {
  id: string;
  name: string;
  dicePool: Die[];
  exhaustedDice: string[];
  slots: Record<string, string | null>;
  cards: FactoryCard[];
  exhaustedCards: string[];
  victoryPoints: number;
  hasPassed: boolean;
}

// v0.1.5 player
export interface DFPlayerV1 {
  id: string;
  name: string;
  dicePool: Die[];
  exhaustedDice: string[];
  freePips: number;
  score: number;
  isReady: boolean;
  effects: string[];
  modifications: string[];
  diceFloor: number;
}

export interface PendingAttack {
  id: string;
  attackerId: string;
  attackerName: string;
  type: 'attack' | 'attackPlus';
  diceCount: number;
  responses: Record<string, string[]>;
  waitingFor: string[];
}

export interface ModBidCard {
  id: string;
  modTypeId: string;
  bids: Array<{ playerId: string; playerName: string; amount: number }>;
  winner: string | null;
}

export interface LogEntry {
  round: number;
  message: string;
  timestamp: number;
}

export interface DFConfig {
  version: 'v0.2.1' | 'v0.1.5' | 'v0.3';
  abilityTiers?: number[];
  selectedAbilities?: string[] | null;
  maxRounds?: number;
}

export type { DFStateV3, DFActionV3 } from './dice-factory-v03';

export interface DFStateV2 {
  type: 'dice-factory';
  version: 'v0.2.1';
  phase: 'playing' | 'ended';
  round: number;
  rng: number;
  nextDieId: number;
  players: DFPlayerV2[];
  turnOrder: string[];
  currentPlayerIndex: number;
  startingPlayerIndex: number;
  actionsRemaining: number;
  tier0Abilities: Ability[];
  availableTier1Abilities: Ability[];
  availableTier2Abilities: Ability[];
  availableTier3Abilities: Ability[];
  availableTier4Abilities: Ability[];
  deck1: FactoryCard[];
  deck2: FactoryCard[];
  deck3: FactoryCard[];
  availableCards: FactoryCard[];
  pendingAttacks: PendingAttack[];
  cardCostReductions: Record<string, number>;
  gameLog: LogEntry[];
  winner: string | null;
}

export interface DFStateV1 {
  type: 'dice-factory';
  version: 'v0.1.5';
  phase: 'playing' | 'complete';
  round: number;
  maxRounds: number;
  rng: number;
  nextDieId: number;
  players: DFPlayerV1[];
  firstStraight: boolean;
  firstSet: boolean;
  modificationDeck: string[];
  currentTurnModifications: ModBidCard[];
  gameLog: LogEntry[];
  winner: string | null;
}

export type DFState = DFStateV2 | DFStateV1 | DFStateV3;

// ===== ACTIONS =====

export type DFActionV2 =
  | { type: 'execute_ability'; playerId: string; abilityId: string; slotNumber: number; costDiceIds: string[]; costCardIds?: string[]; targetDieId?: string | string[]; targetValue?: number; bumpDirection?: 'up' | 'down'; bumpAmount?: number }
  | { type: 'swap_slots'; playerId: string; slot1: number; slot2: number; abilityId?: string; costDiceIds?: string[]; costCardIds?: string[] }
  | { type: 'clear_slot'; playerId: string; slotNumber: number; abilityId?: string; costDiceIds?: string[]; costCardIds?: string[] }
  | { type: 'card_unexhaust'; playerId: string; cardId: string; abilityId?: string; costDiceIds?: string[]; costCardIds?: string[] }
  | { type: 'card_cost_reduction'; playerId: string; cardId: string; abilityId?: string; costDiceIds?: string[]; costCardIds?: string[] }
  | { type: 'swap_plus'; playerId: string; slot1: number; slot2: number; abilityId?: string; costDiceIds?: string[]; costCardIds?: string[] }
  | { type: 'mass_unexhaust'; playerId: string; cardIds: string[]; abilityId?: string; costDiceIds?: string[]; costCardIds?: string[] }
  | { type: 'buy_card'; playerId: string; cardId: string; costDiceIds: string[] }
  | { type: 'assign_slot'; playerId: string; slotNumber: number; abilityId: string | null }
  | { type: 'pass_turn'; playerId: string }
  | { type: 'respond_to_attack'; playerId: string; attackId: string; diceIds: string[] };

export type DFActionV1 =
  | { type: 'recruit_dice'; playerId: string; diceIds: string[] }
  | { type: 'promote_dice'; playerId: string; diceIds: string[] }
  | { type: 'process_dice'; playerId: string; diceIds: string[] }
  | { type: 'modify_die'; playerId: string; dieId: string; change: 1 | -1 }
  | { type: 'reroll_die'; playerId: string; dieId: string }
  | { type: 'score_straight'; playerId: string; diceIds: string[] }
  | { type: 'score_set'; playerId: string; diceIds: string[] }
  | { type: 'place_bid'; playerId: string; modificationId: string; bidAmount: number }
  | { type: 'end_turn'; playerId: string };

export type DFAction = DFActionV2 | DFActionV1 | DFActionV3;

// ===== PLAYER VIEW =====

export interface DFPlayerViewV2 extends DFStateV2 {
  playerId: string;
  playerName: string;
  myDicePool: Die[];
  myExhaustedDice: string[];
  mySlots: Record<string, string | null>;
  myCards: FactoryCard[];
  myExhaustedCards: string[];
  myVictoryPoints: number;
  recruitCost: number;
  targetedRecruitCost: number;
  recruitPlusCost: number;
}

export interface DFPlayerViewV1 extends DFStateV1 {
  playerId: string;
  playerName: string;
  myDicePool: Die[];
  myExhaustedDice: string[];
  myFreePips: number;
  myScore: number;
  myEffects: string[];
  myModifications: string[];
}

export type DFPlayerView = DFPlayerViewV2 | DFPlayerViewV1 | DFStateV3;

export const DEFAULT_CONFIG: DFConfig = {
  version: 'v0.2.1',
  abilityTiers: [1, 2, 3, 4],
  selectedAbilities: null,
};

// ===== HELPERS =====

function clone<T>(val: T): T {
  return JSON.parse(JSON.stringify(val));
}

function makeDieId(state: { nextDieId: number }): [string, number] {
  const id = `die_${state.nextDieId}`;
  return [id, state.nextDieId + 1];
}

function rollDie(rng: number, size: DieSize): [number, number] {
  return rngInt(rng, 1, size);
}

function getSlotTier(slot: number): number {
  if (slot >= 1 && slot <= 4) return 0;
  if (slot >= 5 && slot <= 6) return 1;
  if (slot >= 7 && slot <= 8) return 2;
  if (slot >= 9 && slot <= 10) return 3;
  if (slot >= 11 && slot <= 12) return 4;
  return -1;
}

function log(state: { gameLog: LogEntry[]; round: number }, message: string): LogEntry[] {
  return [...state.gameLog, { round: state.round, message, timestamp: Date.now() }];
}

// ===== v0.2.1 HELPERS =====

function getRecruitCost(player: DFPlayerV2): number {
  return 1 + Math.max(0, player.dicePool.length - 5);
}

function getTargetedRecruitCost(player: DFPlayerV2): number {
  return 1 + Math.max(0, player.dicePool.length - 6);
}

function getRecruitPlusCost(player: DFPlayerV2): number {
  return 1 + Math.max(0, player.dicePool.length - 7);
}

function currentPlayer(state: DFStateV2): DFPlayerV2 {
  return state.players[state.currentPlayerIndex];
}

function getPlayerV2(state: DFStateV2, playerId: string): DFPlayerV2 | undefined {
  return state.players.find(p => p.id === playerId);
}

function findDie(player: DFPlayerV2, dieId: string): Die | undefined {
  return player.dicePool.find(d => d.id === dieId);
}

function isDieExhausted(player: DFPlayerV2, dieId: string): boolean {
  return player.exhaustedDice.includes(dieId);
}

// Roll all dice for all players (used at round start)
function rollAllDiceForAll(state: DFStateV2): DFStateV2 {
  let s = clone(state);
  let rng = s.rng;
  s.players = s.players.map(p => {
    const newPool = p.dicePool.map(d => {
      let value: number;
      [rng, value] = rollDie(rng, d.size as DieSize);
      return { ...d, value };
    });
    return { ...p, dicePool: newPool };
  });
  s.rng = rng;
  return s;
}

// Advance turn: consume one action, possibly advance player
function consumeAction(state: DFStateV2): DFStateV2 {
  let s = clone(state);
  s.actionsRemaining--;

  if (s.actionsRemaining <= 0) {
    return advanceToNextPlayer(s);
  }

  return s;
}

function advanceToNextPlayer(state: DFStateV2): DFStateV2 {
  let s = clone(state);
  const next = getNextPlayerIndex(s);

  if (next === null) {
    return endRound(s);
  }

  s.currentPlayerIndex = next;
  s.actionsRemaining = 2;
  const p = s.players[next];
  s.gameLog = log(s, `--- ${p.name}'s move (2 actions) ---`);
  return s;
}

function getNextPlayerIndex(state: DFStateV2): number | null {
  const total = state.turnOrder.length;
  let idx = (state.currentPlayerIndex + 1) % total;

  for (let i = 0; i < total; i++) {
    const pid = state.turnOrder[idx];
    const p = state.players.find(pl => pl.id === pid);
    if (p && !p.hasPassed) return idx;
    idx = (idx + 1) % total;
  }

  return null;
}

function endRound(state: DFStateV2): DFStateV2 {
  let s = clone(state);
  s.gameLog = log(s, `=== END OF ROUND ${s.round} ===`);

  let rng = s.rng;
  // Refresh all players
  s.players = s.players.map(p => {
    const newPool = p.dicePool.map(d => {
      let value: number;
      [rng, value] = rollDie(rng, d.size as DieSize);
      return { ...d, value };
    });
    return {
      ...p,
      dicePool: newPool,
      exhaustedDice: [],
      exhaustedCards: [],
      hasPassed: false,
    };
  });
  s.rng = rng;

  s.round++;
  s.startingPlayerIndex = (s.startingPlayerIndex + 1) % s.turnOrder.length;
  s.currentPlayerIndex = s.startingPlayerIndex;
  s.actionsRemaining = 2;

  const starter = s.players.find(p => p.id === s.turnOrder[s.startingPlayerIndex]);
  if (starter) {
    s.gameLog = log(s, `=== ROUND ${s.round} ===`);
    s.gameLog = log(s, `--- ${starter.name}'s move (2 actions) ---`);
  }

  return s;
}

// ===== v0.2.1 CREATE GAME =====

function createGameV2(config: DFConfig, players: { id: string; name: string }[], seed: number): DFStateV2 {
  let rng = seed || 1;
  let nextDieId = 1;

  // Shuffle players for turn order
  let shuffledPlayers: { id: string; name: string }[];
  [shuffledPlayers, rng] = shuffleWithRng(players, rng);
  const turnOrder = shuffledPlayers.map(p => p.id);

  // Build initial dice pool for each player
  const gamePlayers: DFPlayerV2[] = shuffledPlayers.map(p => {
    const dicePool: Die[] = [];
    for (let i = 0; i < 4; i++) {
      let value: number;
      [rng, value] = rollDie(rng, 4);
      const id = `die_${nextDieId++}`;
      dicePool.push({ id, size: 4, value });
    }

    return {
      id: p.id,
      name: p.name,
      dicePool,
      exhaustedDice: [],
      slots: {
        '1': 'recruit', '2': 'reroll', '3': 'bump', '4': 'promote',
        '5': null, '6': null, '7': null, '8': null,
        '9': null, '10': null, '11': null, '12': null,
      },
      cards: [],
      exhaustedCards: [],
      victoryPoints: 0,
      hasPassed: false,
    };
  });

  // Shuffle ability decks and pick 3 from each selected tier
  const abilityTiers = config.abilityTiers ?? [1, 2, 3, 4];
  const selectedAbilities = config.selectedAbilities ?? null;

  function drawAbilities(pool: Ability[], count: number): [Ability[], number] {
    let filtered = selectedAbilities
      ? pool.filter(a => selectedAbilities.includes(a.id))
      : [...pool];
    let shuffled: Ability[];
    [shuffled, rng] = shuffleWithRng(filtered, rng);
    return [shuffled.slice(0, count), rng];
  }

  let tier1: Ability[] = [];
  let tier2: Ability[] = [];
  let tier3: Ability[] = [];
  let tier4: Ability[] = [];

  if (abilityTiers.includes(1)) [tier1, rng] = drawAbilities(TIER1_ABILITIES, 3);
  if (abilityTiers.includes(2)) [tier2, rng] = drawAbilities(TIER2_ABILITIES, 3);
  if (abilityTiers.includes(3)) [tier3, rng] = drawAbilities(TIER3_ABILITIES, 3);
  if (abilityTiers.includes(4)) [tier4, rng] = drawAbilities(TIER4_ABILITIES, 3);

  // Shuffle card decks
  let deck1Shuffled: FactoryCard[];
  let deck2Shuffled: FactoryCard[];
  let deck3Shuffled: FactoryCard[];
  [deck1Shuffled, rng] = shuffleWithRng(DECK1_CARDS, rng);
  [deck2Shuffled, rng] = shuffleWithRng(DECK2_CARDS, rng);
  [deck3Shuffled, rng] = shuffleWithRng(DECK3_CARDS, rng);

  // Deal 4 cards from each deck to center
  const availableCards: FactoryCard[] = [];
  const deck1 = deck1Shuffled.slice(4);
  const deck2 = deck2Shuffled.slice(4);
  const deck3 = deck3Shuffled.slice(4);
  for (let i = 0; i < 4; i++) {
    availableCards.push(deck1Shuffled[i]);
    availableCards.push(deck2Shuffled[i]);
    availableCards.push(deck3Shuffled[i]);
  }

  const startingPlayer = gamePlayers[0];
  const gameLog: LogEntry[] = [
    { round: 1, message: '=== DICE FACTORY v0.2.1 ===', timestamp: Date.now() },
    { round: 1, message: `Turn order: ${gamePlayers.map(p => p.name).join(' → ')}`, timestamp: Date.now() },
    { round: 1, message: `--- ${startingPlayer.name}'s move (2 actions) ---`, timestamp: Date.now() },
  ];

  return {
    type: 'dice-factory',
    version: 'v0.2.1',
    phase: 'playing',
    round: 1,
    rng,
    nextDieId,
    players: gamePlayers,
    turnOrder,
    currentPlayerIndex: 0,
    startingPlayerIndex: 0,
    actionsRemaining: 2,
    tier0Abilities: TIER0_ABILITIES,
    availableTier1Abilities: tier1,
    availableTier2Abilities: tier2,
    availableTier3Abilities: tier3,
    availableTier4Abilities: tier4,
    deck1,
    deck2,
    deck3,
    availableCards,
    pendingAttacks: [],
    cardCostReductions: {},
    gameLog,
    winner: null,
  };
}

// ===== v0.2.1 ABILITY EXECUTION =====

type AbilityResult = { ok: true; state: DFStateV2 } | { ok: false; error: string };

function execAbilityEffect(
  state: DFStateV2,
  playerIdx: number,
  abilityId: string,
  targetDieIds: string[],
  targetValue: number | undefined,
  bumpDirection: 'up' | 'down',
  bumpAmount: number,
): AbilityResult {
  let s = clone(state);
  const p = s.players[playerIdx];

  const ability = [...s.tier0Abilities, ...s.availableTier1Abilities, ...s.availableTier2Abilities, ...s.availableTier3Abilities, ...s.availableTier4Abilities].find(a => a.id === abilityId);
  if (!ability) return { ok: false, error: `Unknown ability: ${abilityId}` };

  const effect = ability.effect;
  let rng = s.rng;

  switch (effect) {
    case 'recruit': {
      let value: number;
      const id = `die_${s.nextDieId}`;
      [rng, value] = rollDie(rng, 4);
      s.nextDieId++;
      p.dicePool.push({ id, size: 4, value });
      p.exhaustedDice.push(id);
      s.rng = rng;
      s.gameLog = log(s, `${p.name} recruited d4 (rolled ${value}) - starts exhausted`);
      break;
    }
    case 'reroll': {
      const die = p.dicePool.find(d => d.id === targetDieIds[0]);
      if (!die) return { ok: false, error: 'Target die not found' };
      const oldVal = die.value;
      let val: number;
      [rng, val] = rollDie(rng, die.size as DieSize);
      die.value = val;
      s.rng = rng;
      s.gameLog = log(s, `${p.name} rerolled d${die.size} from ${oldVal} to ${val}`);
      break;
    }
    case 'bump': {
      const die = p.dicePool.find(d => d.id === targetDieIds[0]);
      if (!die) return { ok: false, error: 'Target die not found' };
      if (bumpDirection === 'up') {
        if (die.value >= die.size) return { ok: false, error: 'Die already at maximum' };
        die.value++;
      } else {
        if (die.value <= 1) return { ok: false, error: 'Die already at minimum' };
        die.value--;
      }
      s.gameLog = log(s, `${p.name} bumped d${die.size} ${bumpDirection} to ${die.value}`);
      break;
    }
    case 'promote': {
      const die = p.dicePool.find(d => d.id === targetDieIds[0]);
      if (!die) return { ok: false, error: 'Target die not found' };
      const sizeMap: Record<number, DieSize> = { 4: 6, 6: 8, 8: 10, 10: 12 };
      const nextSize = sizeMap[die.size];
      if (!nextSize) return { ok: false, error: 'Die already at maximum size' };
      const oldSize = die.size;
      let val: number;
      [rng, val] = rollDie(rng, nextSize);
      die.size = nextSize;
      die.value = val;
      s.rng = rng;
      s.gameLog = log(s, `${p.name} promoted d${oldSize} to d${nextSize} (rolled ${val})`);
      break;
    }
    case 'bumpUp': {
      const die = p.dicePool.find(d => d.id === targetDieIds[0]);
      if (!die) return { ok: false, error: 'Target die not found' };
      const amt = bumpAmount || 1;
      if (die.value + amt > die.size) return { ok: false, error: `Cannot bump up by ${amt}` };
      die.value += amt;
      s.gameLog = log(s, `${p.name} bumped up d${die.size} by ${amt} to ${die.value}`);
      break;
    }
    case 'bumpDown': {
      const die = p.dicePool.find(d => d.id === targetDieIds[0]);
      if (!die) return { ok: false, error: 'Target die not found' };
      const amt = bumpAmount || 1;
      if (die.value - amt < 1) return { ok: false, error: `Cannot bump down by ${amt}` };
      die.value -= amt;
      s.gameLog = log(s, `${p.name} bumped down d${die.size} by ${amt} to ${die.value}`);
      break;
    }
    case 'massReroll': {
      const results: string[] = [];
      let r = rng;
      for (const dieId of targetDieIds) {
        const die = p.dicePool.find(d => d.id === dieId);
        if (!die) return { ok: false, error: 'Target die not found' };
        const old = die.value;
        let val: number;
        [r, val] = rollDie(r, die.size as DieSize);
        die.value = val;
        results.push(`d${die.size}: ${old}→${val}`);
      }
      rng = r;
      s.rng = rng;
      s.gameLog = log(s, `${p.name} used Mass Reroll: ${results.join(', ')}`);
      break;
    }
    case 'attack':
    case 'attackPlus': {
      const others = s.players.filter(pl => pl.id !== p.id);
      if (others.length === 0) {
        s.gameLog = log(s, `${p.name} used ${effect} (no opponents)`);
        break;
      }
      const attackId = `atk_${s.nextDieId++}`;
      const atk: PendingAttack = {
        id: attackId,
        attackerId: p.id,
        attackerName: p.name,
        type: effect === 'attack' ? 'attack' : 'attackPlus',
        diceCount: effect === 'attack' ? 1 : 2,
        responses: {},
        waitingFor: others.map(pl => pl.id),
      };
      s.pendingAttacks.push(atk);
      s.gameLog = log(s, `${p.name} used ${effect === 'attack' ? 'Attack' : 'Attack+'}`);
      break;
    }
    case 'massBump': {
      const results: string[] = [];
      for (const dieId of targetDieIds) {
        const die = p.dicePool.find(d => d.id === dieId);
        if (!die) return { ok: false, error: 'Target die not found' };
        if (bumpDirection === 'up') {
          if (die.value >= die.size) return { ok: false, error: `Cannot bump d${die.size} up` };
          die.value++;
        } else {
          if (die.value <= 1) return { ok: false, error: `Cannot bump d${die.size} down` };
          die.value--;
        }
        results.push(`d${die.size}→${die.value}`);
      }
      s.gameLog = log(s, `${p.name} used Mass Bump ${bumpDirection}: ${results.join(', ')}`);
      break;
    }
    case 'twosReroll': {
      const twos = p.dicePool.filter(d => d.value === 2);
      let r = rng;
      const results: string[] = [];
      for (const die of twos) {
        let val: number;
        [r, val] = rollDie(r, die.size as DieSize);
        results.push(`d${die.size}: 2→${val}`);
        die.value = val;
      }
      rng = r;
      s.rng = rng;
      s.gameLog = log(s, `${p.name} used 2s Reroll (${twos.length} dice)${results.length ? ': ' + results.join(', ') : ''}`);
      break;
    }
    case 'score': {
      p.victoryPoints += 1;
      s.gameLog = log(s, `${p.name} used Score: +1 VP (total: ${p.victoryPoints})`);
      if (p.victoryPoints >= 10) {
        s.phase = 'ended';
        s.winner = p.name;
        s.gameLog = log(s, `🎉 ${p.name} WINS with ${p.victoryPoints} VP!`);
      }
      break;
    }
    case 'targetedRecruit': {
      const tgt = p.dicePool.find(d => d.id === targetDieIds[0]);
      if (!tgt) return { ok: false, error: 'Target die not found' };
      const id = `die_${s.nextDieId++}`;
      let val: number;
      [rng, val] = rollDie(rng, tgt.size as DieSize);
      p.dicePool.push({ id, size: tgt.size, value: val });
      p.exhaustedDice.push(id);
      s.rng = rng;
      s.gameLog = log(s, `${p.name} used Targeted Recruit: d${tgt.size} (rolled ${val}) - exhausted`);
      break;
    }
    case 'recruitPlus': {
      const id = `die_${s.nextDieId++}`;
      let val: number;
      [rng, val] = rollDie(rng, 4);
      p.dicePool.push({ id, size: 4, value: val });
      p.exhaustedDice.push(id);
      s.rng = rng;
      s.gameLog = log(s, `${p.name} used Recruit+: d4 (rolled ${val}) - exhausted`);
      break;
    }
    case 'unexhaust': {
      const dieId = targetDieIds[0];
      if (!p.exhaustedDice.includes(dieId)) return { ok: false, error: 'Die is not exhausted' };
      p.exhaustedDice = p.exhaustedDice.filter(id => id !== dieId);
      const die = p.dicePool.find(d => d.id === dieId);
      s.gameLog = log(s, `${p.name} unexhausted d${die?.size ?? '?'}`);
      break;
    }
    case 'swapSlots': {
      // swapSlots is a separate action type for cleaner handling
      return { ok: false, error: 'Use swap_slots action type' };
    }
    case 'scorePlus': {
      p.victoryPoints += 2;
      s.gameLog = log(s, `${p.name} used Score+: +2 VP (total: ${p.victoryPoints})`);
      if (p.victoryPoints >= 10) {
        s.phase = 'ended';
        s.winner = p.name;
        s.gameLog = log(s, `🎉 ${p.name} WINS with ${p.victoryPoints} VP!`);
      }
      break;
    }
    case 'massBumpPlus': {
      const amt = bumpAmount || 1;
      const results: string[] = [];
      for (const dieId of targetDieIds) {
        const die = p.dicePool.find(d => d.id === dieId);
        if (!die) return { ok: false, error: 'Target die not found' };
        if (bumpDirection === 'up') {
          if (die.value + amt > die.size) return { ok: false, error: `Cannot bump d${die.size} up by ${amt}` };
          die.value += amt;
        } else {
          if (die.value - amt < 1) return { ok: false, error: `Cannot bump d${die.size} down by ${amt}` };
          die.value -= amt;
        }
        results.push(`d${die.size}→${die.value}`);
      }
      s.gameLog = log(s, `${p.name} used Mass Bump+ ${bumpDirection} by ${amt}: ${results.join(', ')}`);
      break;
    }
    case 'massRerollPlus': {
      const results: string[] = [];
      let r = rng;
      for (const dieId of targetDieIds) {
        const die = p.dicePool.find(d => d.id === dieId);
        if (!die) return { ok: false, error: 'Target die not found' };
        const old = die.value;
        let val: number;
        [r, val] = rollDie(r, die.size as DieSize);
        die.value = val;
        results.push(`d${die.size}: ${old}→${val}`);
      }
      rng = r;
      s.rng = rng;
      s.gameLog = log(s, `${p.name} used Mass Reroll+ (${targetDieIds.length} dice): ${results.join(', ')}`);
      break;
    }
    case 'clearSlot':
    case 'cardUnexhaust':
    case 'cardCostReduction':
    case 'swapPlus':
    case 'massUnexhaust':
      return { ok: false, error: `Use dedicated action type for ${effect}` };
    case 'scorePlusPlus': {
      p.victoryPoints += 3;
      s.gameLog = log(s, `${p.name} used Score++: +3 VP (total: ${p.victoryPoints})`);
      if (p.victoryPoints >= 10) {
        s.phase = 'ended';
        s.winner = p.name;
        s.gameLog = log(s, `🎉 ${p.name} WINS with ${p.victoryPoints} VP!`);
      }
      break;
    }
    case 'targetAttack': {
      const targetDie = p.dicePool.find(d => d.id === targetDieIds[0]);
      if (!targetDie) return { ok: false, error: 'Target die not found' };
      const tv = targetDie.value;
      for (const pl of s.players) {
        const matching = pl.dicePool.filter(d => d.value === tv);
        for (const d of matching) {
          if (!pl.exhaustedDice.includes(d.id)) pl.exhaustedDice.push(d.id);
        }
        if (matching.length > 0) {
          s.gameLog = log(s, `${pl.name} exhausted ${matching.length} dice showing ${tv} from Target Attack`);
        }
      }
      s.gameLog = log(s, `${p.name} used Target Attack on value ${tv}`);
      break;
    }
    case 'targetedUnexhaust': {
      const tgt = p.dicePool.find(d => d.id === targetDieIds[0]);
      if (!tgt) return { ok: false, error: 'Target die not found' };
      const tv = tgt.value;
      const matching = p.dicePool.filter(d => d.value === tv && p.exhaustedDice.includes(d.id));
      p.exhaustedDice = p.exhaustedDice.filter(id => {
        const die = p.dicePool.find(d => d.id === id);
        return !(die && die.value === tv);
      });
      s.gameLog = log(s, `${p.name} used Targeted Unexhaust: unexhausted ${matching.length} dice showing ${tv}`);
      break;
    }
    case 'selectValue': {
      const die = p.dicePool.find(d => d.id === targetDieIds[0]);
      if (!die) return { ok: false, error: 'Target die not found' };
      if (targetValue === undefined || targetValue < 1 || targetValue > die.size) {
        return { ok: false, error: `Value must be 1-${die.size}` };
      }
      die.value = targetValue;
      s.gameLog = log(s, `${p.name} set d${die.size} to ${targetValue}`);
      break;
    }
    default:
      return { ok: false, error: `Unknown effect: ${effect}` };
  }

  return { ok: true, state: s };
}

// ===== v0.2.1 APPLY ACTION =====

function applyActionV2(state: DFStateV2, action: DFActionV2): DFStateV2 {
  if (state.phase === 'ended') return state;

  const s = clone(state);

  if (action.type === 'respond_to_attack') {
    return handleRespondToAttack(s, action.playerId, action.attackId, action.diceIds);
  }

  if (action.type === 'assign_slot') {
    return handleAssignSlot(s, action.playerId, action.slotNumber, action.abilityId);
  }

  // All other actions require it to be the player's turn
  const cp = s.players[s.currentPlayerIndex];
  if (!cp || cp.id !== action.playerId) return state;
  if (s.actionsRemaining <= 0) return state;

  const playerIdx = s.currentPlayerIndex;

  switch (action.type) {
    case 'pass_turn': {
      const p = s.players[playerIdx];
      p.hasPassed = true;
      s.gameLog = log(s, `${p.name} has passed`);
      return advanceToNextPlayer(s);
    }

    case 'execute_ability': {
      const p = s.players[playerIdx];
      const ability = [...s.tier0Abilities, ...s.availableTier1Abilities, ...s.availableTier2Abilities, ...s.availableTier3Abilities, ...s.availableTier4Abilities].find(a => a.id === action.abilityId);
      if (!ability) return state;

      const slotAbility = p.slots[String(action.slotNumber)];
      if (slotAbility !== action.abilityId) return state;

      const costDiceIds = action.costDiceIds || [];
      const costCardIds = action.costCardIds || [];
      const targetDieIds = Array.isArray(action.targetDieId)
        ? action.targetDieId
        : action.targetDieId ? [action.targetDieId] : [];

      // Validate cost dice
      for (const dieId of costDiceIds) {
        const die = p.dicePool.find(d => d.id === dieId);
        if (!die || p.exhaustedDice.includes(dieId)) return state;
        if (die.value !== action.slotNumber) return state;
      }

      // Validate cost cards
      for (const cardId of costCardIds) {
        const card = p.cards.find(c => c.id === cardId);
        if (!card || p.exhaustedCards.includes(cardId)) return state;
        if (!card.effects.diceNumbers.includes(action.slotNumber)) return state;
      }

      // Validate total cost
      let requiredCost = ability.costCount;
      if (ability.id === 'recruit') requiredCost = getRecruitCost(p);
      else if (ability.effect === 'targetedRecruit') requiredCost = getTargetedRecruitCost(p);
      else if (ability.effect === 'recruitPlus') requiredCost = getRecruitPlusCost(p);

      if (costDiceIds.length + costCardIds.length !== requiredCost) return state;

      // Effects that need separate action types
      if (['swapSlots', 'clearSlot', 'cardUnexhaust', 'cardCostReduction', 'swapPlus', 'massUnexhaust'].includes(ability.effect)) {
        return state;
      }

      const result = execAbilityEffect(
        s, playerIdx, action.abilityId, targetDieIds,
        action.targetValue, action.bumpDirection ?? 'up', action.bumpAmount ?? 1,
      );
      if (!result.ok) return state;

      let ns = result.state;
      const np = ns.players[playerIdx];

      // Exhaust cost
      costDiceIds.forEach(id => { if (!np.exhaustedDice.includes(id)) np.exhaustedDice.push(id); });
      costCardIds.forEach(id => { if (!np.exhaustedCards.includes(id)) np.exhaustedCards.push(id); });

      // Exhaust target if required
      if (ability.exhaustTarget) {
        targetDieIds.forEach(id => { if (!np.exhaustedDice.includes(id)) np.exhaustedDice.push(id); });
      }

      return consumeAction(ns);
    }

    case 'swap_slots': {
      return handleSwapSlots(s, playerIdx, action.slot1, action.slot2, false, action.abilityId, action.costDiceIds, action.costCardIds);
    }

    case 'clear_slot': {
      return handleClearSlot(s, playerIdx, action.slotNumber, action.abilityId, action.costDiceIds, action.costCardIds);
    }

    case 'card_unexhaust': {
      return handleCardUnexhaust(s, playerIdx, action.cardId, action.abilityId, action.costDiceIds, action.costCardIds);
    }

    case 'card_cost_reduction': {
      return handleCardCostReduction(s, playerIdx, action.cardId, action.abilityId, action.costDiceIds, action.costCardIds);
    }

    case 'swap_plus': {
      return handleSwapSlots(s, playerIdx, action.slot1, action.slot2, true, action.abilityId, action.costDiceIds, action.costCardIds);
    }

    case 'mass_unexhaust': {
      return handleMassUnexhaust(s, playerIdx, action.cardIds, action.abilityId, action.costDiceIds, action.costCardIds);
    }

    case 'buy_card': {
      return handleBuyCard(s, playerIdx, action.cardId, action.costDiceIds);
    }

    default:
      return state;
  }
}

function handleAssignSlot(state: DFStateV2, playerId: string, slotNumber: number, abilityId: string | null): DFStateV2 {
  const s = clone(state);
  const p = s.players.find(pl => pl.id === playerId);
  if (!p) return state;

  if (!abilityId) {
    p.slots[String(slotNumber)] = null;
    s.gameLog = log(s, `${p.name} cleared slot ${slotNumber}`);
    return s;
  }

  const ability = [...s.tier0Abilities, ...s.availableTier1Abilities, ...s.availableTier2Abilities, ...s.availableTier3Abilities, ...s.availableTier4Abilities].find(a => a.id === abilityId);
  if (!ability) return state;

  const slotTier = getSlotTier(slotNumber);
  if (ability.tier > slotTier) return state;

  p.slots[String(slotNumber)] = abilityId;
  s.gameLog = log(s, `${p.name} assigned ${ability.name} to slot ${slotNumber}`);
  return s;
}

function handleRespondToAttack(state: DFStateV2, playerId: string, attackId: string, diceIds: string[]): DFStateV2 {
  const s = clone(state);
  const atk = s.pendingAttacks.find(a => a.id === attackId);
  if (!atk || !atk.waitingFor.includes(playerId)) return state;

  const p = s.players.find(pl => pl.id === playerId);
  if (!p) return state;

  const available = p.dicePool.filter(d => !p.exhaustedDice.includes(d.id));
  const maxDice = Math.min(atk.diceCount, available.length);
  if (diceIds.length > maxDice) return state;

  for (const id of diceIds) {
    if (!p.dicePool.find(d => d.id === id) || p.exhaustedDice.includes(id)) return state;
  }

  atk.responses[playerId] = diceIds;
  atk.waitingFor = atk.waitingFor.filter(id => id !== playerId);

  if (atk.waitingFor.length === 0) {
    // Execute the attack
    for (const [pid, ids] of Object.entries(atk.responses)) {
      const pl = s.players.find(pp => pp.id === pid);
      if (!pl) continue;
      for (const id of ids) {
        if (!pl.exhaustedDice.includes(id)) pl.exhaustedDice.push(id);
      }
      if (ids.length > 0) {
        s.gameLog = log(s, `${pl.name} exhausted ${ids.length} dice from ${atk.type === 'attackPlus' ? 'Attack+' : 'Attack'}`);
      }
    }
    s.pendingAttacks = s.pendingAttacks.filter(a => a.id !== attackId);
  }

  return s;
}

function handleSwapSlots(
  state: DFStateV2,
  playerIdx: number,
  slot1: number,
  slot2: number,
  noTierCheck: boolean,
  abilityId?: string,
  costDiceIds?: string[],
  costCardIds?: string[],
): DFStateV2 {
  const s = clone(state);
  const p = s.players[playerIdx];
  if (!p) return state;

  if (slot1 < 1 || slot1 > 12 || slot2 < 1 || slot2 > 12 || slot1 === slot2) return state;

  const key1 = String(slot1);
  const key2 = String(slot2);
  const ab1 = p.slots[key1];
  const ab2 = p.slots[key2];

  if (!ab1 && !ab2) return state;

  if (!noTierCheck) {
    if (ab1) {
      const a = [...s.tier0Abilities, ...s.availableTier1Abilities, ...s.availableTier2Abilities, ...s.availableTier3Abilities, ...s.availableTier4Abilities].find(a => a.id === ab1);
      if (a && a.tier > getSlotTier(slot2)) return state;
    }
    if (ab2) {
      const a = [...s.tier0Abilities, ...s.availableTier1Abilities, ...s.availableTier2Abilities, ...s.availableTier3Abilities, ...s.availableTier4Abilities].find(a => a.id === ab2);
      if (a && a.tier > getSlotTier(slot1)) return state;
    }
  }

  // Validate and exhaust cost
  if (abilityId && (costDiceIds || costCardIds)) {
    if (!validateAndExhaustCost(s, p, abilityId, slot1, costDiceIds ?? [], costCardIds ?? [])) return state;
  }

  p.slots[key1] = ab2 ?? null;
  p.slots[key2] = ab1 ?? null;

  const name1 = ab1 ? ([...s.tier0Abilities, ...s.availableTier1Abilities, ...s.availableTier2Abilities, ...s.availableTier3Abilities, ...s.availableTier4Abilities].find(a => a.id === ab1)?.name ?? ab1) : 'Empty';
  const name2 = ab2 ? ([...s.tier0Abilities, ...s.availableTier1Abilities, ...s.availableTier2Abilities, ...s.availableTier3Abilities, ...s.availableTier4Abilities].find(a => a.id === ab2)?.name ?? ab2) : 'Empty';
  s.gameLog = log(s, `${p.name} ${noTierCheck ? 'used Swap+' : 'swapped'} slots ${slot1} (${name1}) ↔ ${slot2} (${name2})`);

  return consumeAction(s);
}

function validateAndExhaustCost(
  state: DFStateV2,
  p: DFPlayerV2,
  abilityId: string,
  slotNumber: number,
  costDiceIds: string[],
  costCardIds: string[],
): boolean {
  for (const dieId of costDiceIds) {
    const die = p.dicePool.find(d => d.id === dieId);
    if (!die || p.exhaustedDice.includes(dieId)) return false;
    if (die.value !== slotNumber) return false;
    p.exhaustedDice.push(dieId);
  }
  for (const cardId of costCardIds) {
    const card = p.cards.find(c => c.id === cardId);
    if (!card || p.exhaustedCards.includes(cardId)) return false;
    if (!card.effects.diceNumbers.includes(slotNumber)) return false;
    p.exhaustedCards.push(cardId);
  }
  return true;
}

function handleClearSlot(
  state: DFStateV2,
  playerIdx: number,
  slotNumber: number,
  abilityId?: string,
  costDiceIds?: string[],
  costCardIds?: string[],
): DFStateV2 {
  const s = clone(state);
  const p = s.players[playerIdx];
  const key = String(slotNumber);
  if (!p || !p.slots[key]) return state;

  if (abilityId && (costDiceIds || costCardIds)) {
    if (!validateAndExhaustCost(s, p, abilityId, slotNumber, costDiceIds ?? [], costCardIds ?? [])) return state;
  }

  const abilityName = p.slots[key] ?? '';
  p.slots[key] = null;
  s.gameLog = log(s, `${p.name} cleared slot ${slotNumber} (${abilityName})`);
  return consumeAction(s);
}

function handleCardUnexhaust(
  state: DFStateV2,
  playerIdx: number,
  cardId: string,
  abilityId?: string,
  costDiceIds?: string[],
  costCardIds?: string[],
): DFStateV2 {
  const s = clone(state);
  const p = s.players[playerIdx];
  if (!p) return state;
  const card = p.cards.find(c => c.id === cardId);
  if (!card || !p.exhaustedCards.includes(cardId)) return state;

  if (abilityId && (costDiceIds || costCardIds)) {
    // find the slot with this ability
    const slotEntry = Object.entries(p.slots).find(([, v]) => v === abilityId);
    const slotNumber = slotEntry ? parseInt(slotEntry[0]) : 0;
    if (!validateAndExhaustCost(s, p, abilityId, slotNumber, costDiceIds ?? [], costCardIds ?? [])) return state;
  }

  p.exhaustedCards = p.exhaustedCards.filter(id => id !== cardId);
  s.gameLog = log(s, `${p.name} unexhausted card: ${card.title}`);
  return consumeAction(s);
}

function handleCardCostReduction(
  state: DFStateV2,
  playerIdx: number,
  cardId: string,
  abilityId?: string,
  costDiceIds?: string[],
  costCardIds?: string[],
): DFStateV2 {
  const s = clone(state);
  const p = s.players[playerIdx];
  if (!p) return state;
  const card = s.availableCards.find(c => c.id === cardId);
  if (!card) return state;

  if (abilityId && (costDiceIds || costCardIds)) {
    const slotEntry = Object.entries(p.slots).find(([, v]) => v === abilityId);
    const slotNumber = slotEntry ? parseInt(slotEntry[0]) : 0;
    if (!validateAndExhaustCost(s, p, abilityId, slotNumber, costDiceIds ?? [], costCardIds ?? [])) return state;
  }

  const reductions = s.cardCostReductions[cardId] ?? 0;
  const currentCost = Math.max(0, card.cost.length - reductions);
  if (currentCost === 0) return state;

  s.cardCostReductions = { ...s.cardCostReductions, [cardId]: reductions + 1 };
  s.gameLog = log(s, `${p.name} reduced cost of ${card.title}: ${currentCost}→${currentCost - 1} dice required`);
  return consumeAction(s);
}

function handleMassUnexhaust(
  state: DFStateV2,
  playerIdx: number,
  cardIds: string[],
  abilityId?: string,
  costDiceIds?: string[],
  costCardIds?: string[],
): DFStateV2 {
  const s = clone(state);
  const p = s.players[playerIdx];
  if (!p || cardIds.length === 0 || cardIds.length > 2) return state;

  for (const cardId of cardIds) {
    if (!p.cards.find(c => c.id === cardId) || !p.exhaustedCards.includes(cardId)) return state;
  }

  if (abilityId && (costDiceIds || costCardIds)) {
    const slotEntry = Object.entries(p.slots).find(([, v]) => v === abilityId);
    const slotNumber = slotEntry ? parseInt(slotEntry[0]) : 0;
    if (!validateAndExhaustCost(s, p, abilityId, slotNumber, costDiceIds ?? [], costCardIds ?? [])) return state;
  }

  p.exhaustedCards = p.exhaustedCards.filter(id => !cardIds.includes(id));
  const names = cardIds.map(id => p.cards.find(c => c.id === id)?.title ?? id).join(', ');
  s.gameLog = log(s, `${p.name} unexhausted ${cardIds.length} card(s): ${names}`);
  return consumeAction(s);
}

function handleBuyCard(state: DFStateV2, playerIdx: number, cardId: string, costDiceIds: string[]): DFStateV2 {
  const s = clone(state);
  const p = s.players[playerIdx];
  if (!p) return state;

  const cardIdx = s.availableCards.findIndex(c => c.id === cardId);
  if (cardIdx === -1) return state;

  const card = s.availableCards[cardIdx];
  const reductions = s.cardCostReductions[cardId] ?? 0;

  // Build required cost after reductions
  const fullCost = [...card.cost].sort((a, b) => a - b);
  const reducedCount = Math.max(0, fullCost.length - reductions);
  const requiredCost = fullCost.slice(fullCost.length - reducedCount);

  if (costDiceIds.length !== requiredCost.length) return state;

  const providedValues = costDiceIds.map(id => {
    const die = p.dicePool.find(d => d.id === id);
    if (!die || p.exhaustedDice.includes(id)) return null;
    return die.value;
  }).filter((v): v is number => v !== null).sort((a, b) => a - b);

  if (providedValues.length !== requiredCost.length) return state;
  for (let i = 0; i < requiredCost.length; i++) {
    if (requiredCost[i] !== providedValues[i]) return state;
  }

  // Exhaust cost dice
  costDiceIds.forEach(id => { if (!p.exhaustedDice.includes(id)) p.exhaustedDice.push(id); });

  // Move card from available to player's hand
  p.cards.push(card);
  s.availableCards.splice(cardIdx, 1);

  // Replace from deck
  if (card.deckSource === 1 && s.deck1.length > 0) {
    s.availableCards.push(s.deck1.shift()!);
  } else if (card.deckSource === 2 && s.deck2.length > 0) {
    s.availableCards.push(s.deck2.shift()!);
  } else if (card.deckSource === 3 && s.deck3.length > 0) {
    s.availableCards.push(s.deck3.shift()!);
  }

  if (card.effects.vp) {
    p.victoryPoints += card.effects.vp;
    s.gameLog = log(s, `${p.name} bought "${card.title}" (+${card.effects.vp} VP)`);
  } else {
    s.gameLog = log(s, `${p.name} bought "${card.title}"`);
  }

  // Delete cost reduction since card is gone from center
  const newReductions = { ...s.cardCostReductions };
  delete newReductions[cardId];
  s.cardCostReductions = newReductions;

  if (p.victoryPoints >= 10) {
    s.phase = 'ended';
    s.winner = p.name;
    s.gameLog = log(s, `🎉 ${p.name} WINS with ${p.victoryPoints} VP!`);
  }

  return consumeAction(s);
}

// ===== v0.1.5 =====

const FACTORY_MODIFICATIONS = [
  { id: 'dice_pool_size', name: 'Dice Pool Size', description: 'Increase your die pool by 1', stackable: true, count: 5 },
  { id: 'outsourcing', name: 'Outsourcing', description: 'Recruit 1 die regardless of roll value; recruits same size.', stackable: false, count: 5 },
  { id: 'synergy', name: 'Synergy', description: 'Tricks count as 1 additional die.', stackable: false, count: 4 },
  { id: 'cash_flow_enhancement', name: 'Cash Flow Enhancement', description: 'First processed die gives 3X pips.', stackable: false, count: 4 },
  { id: 'quality_control', name: 'Quality Control', description: 'Reroll one die for free each turn.', stackable: false, count: 3 },
  { id: 'dice_pool_upgrade', name: 'Dice Pool Upgrade', description: 'Your pool provides d6s minimum.', stackable: false, count: 3 },
  { id: 'variable_dice_pool', name: 'Variable Dice Pool', description: 'Spend 10 pips to increase die pool.', stackable: false, count: 3 },
  { id: 'patent_protection', name: 'Patent Protection', description: 'When scoring, keep highest die.', stackable: false, count: 2 },
  { id: 'market_manipulation', name: 'Market Manipulation', description: 'Effects/Mods cost 4 pips less.', stackable: false, count: 2 },
  { id: 'dividend', name: 'Dividend', description: 'End-of-turn die can give points instead of pips.', stackable: false, count: 2 },
  { id: 'arbitrage', name: 'Arbitrage', description: 'Process a die for 2X VP instead of pips.', stackable: false, count: 2 },
  { id: 'dice_tower', name: 'Dice Tower', description: 'Start of turn: may reroll all dice.', stackable: false, count: 2 },
  { id: 'improved_rollers', name: 'Improved Rollers', description: 'Rerolls cost 1 pip.', stackable: false, count: 1 },
  { id: 'roller_derby', name: 'Roller Derby', description: 'May recruit a free d4; everyone else also recruits a d4.', stackable: false, count: 1 },
  { id: 'diversification', name: 'Diversification', description: 'Recruit a d4 off rolling 2 on a d4.', stackable: false, count: 1 },
  { id: 'due_diligence', name: 'Due Diligence', description: 'Increasing die value costs 3 pips.', stackable: false, count: 1 },
  { id: 'joint_venture', name: 'Joint Venture', description: 'Sets may use 2 die values.', stackable: false, count: 1 },
  { id: 'vertical_integration', name: 'Vertical Integration', description: 'Straights may skip a number.', stackable: false, count: 1 },
  { id: 'corporate_debt', name: 'Corporate Debt', description: 'Go negative pips (to -20); lose points per round equal to debt.', stackable: false, count: 1 },
  { id: 'tworus', name: '2rUS', description: 'Reroll all 2s automatically.', stackable: false, count: 1 },
];

function createModificationDeck(rng: number): [string[], number] {
  const deck: string[] = [];
  for (const mod of FACTORY_MODIFICATIONS) {
    for (let i = 0; i < mod.count; i++) deck.push(mod.id);
  }
  return shuffleWithRng(deck, rng);
}

function drawTurnModifications(deck: string[], rng: number, counter: number): [ModBidCard[], string[], number, number] {
  const cards = deck.slice(0, 3);
  const remaining = deck.slice(3);
  let instanceCounter = counter;
  const modCards: ModBidCard[] = cards.map(modTypeId => ({
    id: `mod_${instanceCounter++}`,
    modTypeId,
    bids: [],
    winner: null,
  }));
  return [modCards, remaining, rng, instanceCounter];
}

function createGameV1(config: DFConfig, players: { id: string; name: string }[], seed: number): DFStateV1 {
  let rng = seed || 1;
  let nextDieId = 1;

  const gamePlayers: DFPlayerV1[] = players.map(p => {
    const dicePool: Die[] = [];
    for (let i = 0; i < 4; i++) {
      let value: number;
      [rng, value] = rollDie(rng, 4);
      dicePool.push({ id: `die_${nextDieId++}`, size: 4, value });
    }
    return {
      id: p.id,
      name: p.name,
      dicePool,
      exhaustedDice: [],
      freePips: 9,
      score: 0,
      isReady: false,
      effects: [],
      modifications: [],
      diceFloor: 4,
    };
  });

  let modDeck: string[];
  [modDeck, rng] = createModificationDeck(rng);
  let modCards: ModBidCard[];
  let counter = 1;
  [modCards, modDeck, rng, counter] = drawTurnModifications(modDeck, rng, counter);

  return {
    type: 'dice-factory',
    version: 'v0.1.5',
    phase: 'playing',
    round: 1,
    maxRounds: config.maxRounds ?? 11,
    rng,
    nextDieId,
    players: gamePlayers,
    firstStraight: false,
    firstSet: false,
    modificationDeck: modDeck,
    currentTurnModifications: modCards,
    gameLog: [{ round: 1, message: '=== DICE FACTORY v0.1.5 ===', timestamp: Date.now() }],
    winner: null,
  };
}

function applyActionV1(state: DFStateV1, action: DFActionV1): DFStateV1 {
  if (state.phase === 'complete') return state;

  const s = clone(state);
  const p = s.players.find((pl: DFPlayerV1) => pl.id === action.playerId);
  if (!p) return state;

  switch (action.type) {
    case 'recruit_dice': {
      if (p.isReady) return state;
      const DICE_PROG: DieSize[] = [4, 6, 8, 10, 12];
      let rng = s.rng;

      for (const dieId of action.diceIds) {
        const die = p.dicePool.find((d: Die) => d.id === dieId);
        if (!die || p.exhaustedDice.includes(dieId)) return state;

        const hasOutsourcing = p.modifications.includes('outsourcing');
        const hasDiversification = p.modifications.includes('diversification');

        let canRecruit = false;
        let recruitSize: DieSize = 4;

        if (hasOutsourcing) {
          canRecruit = true;
          recruitSize = die.size as DieSize;
        } else {
          const idx = DICE_PROG.indexOf(die.size as DieSize);
          const canOnTwo = hasDiversification && die.size === 4 && die.value === 2;
          const recruitVals = Array.from({ length: idx + 1 }, (_, i) => i + 1);
          if (recruitVals.includes(die.value) || canOnTwo) {
            canRecruit = true;
            const targetIdx = Math.max(0, idx - (die.value - 1));
            recruitSize = DICE_PROG[targetIdx];
          }
        }

        if (!canRecruit) return state;

        let value: number;
        [rng, value] = rollDie(rng, recruitSize);
        const id = `die_${s.nextDieId++}`;
        p.dicePool.push({ id, size: recruitSize, value });
        p.exhaustedDice.push(dieId);
        s.gameLog = log(s, `${p.name} recruited d${recruitSize} (rolled ${value})`);
      }
      s.rng = rng;
      break;
    }

    case 'promote_dice': {
      if (p.isReady) return state;
      const SIZE_MAP: Record<number, DieSize> = { 4: 6, 6: 8, 8: 10, 10: 12 };
      let rng = s.rng;

      for (const dieId of action.diceIds) {
        const die = p.dicePool.find((d: Die) => d.id === dieId);
        if (!die || p.exhaustedDice.includes(dieId)) return state;
        const nextSize = SIZE_MAP[die.size];
        if (!nextSize) return state;
        const old = die.size;
        let val: number;
        [rng, val] = rollDie(rng, nextSize);
        die.size = nextSize;
        die.value = val;
        p.exhaustedDice.push(dieId);
        s.gameLog = log(s, `${p.name} promoted d${old} to d${nextSize} (rolled ${val})`);
      }
      s.rng = rng;
      break;
    }

    case 'process_dice': {
      if (p.isReady) return state;
      let pips = 0;
      for (const dieId of action.diceIds) {
        const dieIdx = p.dicePool.findIndex((d: Die) => d.id === dieId);
        if (dieIdx === -1 || p.exhaustedDice.includes(dieId)) return state;
        const die = p.dicePool[dieIdx];
        pips += die.value * 2;
        p.dicePool.splice(dieIdx, 1);
      }
      p.freePips += pips;
      s.gameLog = log(s, `${p.name} processed ${action.diceIds.length} dice for ${pips} pips`);
      break;
    }

    case 'modify_die': {
      if (p.isReady) return state;
      const die = p.dicePool.find((d: Die) => d.id === action.dieId);
      if (!die || p.exhaustedDice.includes(action.dieId)) return state;
      const hasDueDiligence = p.modifications.includes('due_diligence');
      const costIncrease = hasDueDiligence ? 3 : 4;
      const costDecrease = 3;
      const cost = action.change > 0 ? costIncrease : costDecrease;
      if (p.freePips < cost) return state;
      if (action.change > 0 && die.value >= die.size) return state;
      if (action.change < 0 && die.value <= 1) return state;
      die.value += action.change;
      p.freePips -= cost;
      s.gameLog = log(s, `${p.name} modified d${die.size} to ${die.value} (-${cost} pips)`);
      break;
    }

    case 'reroll_die': {
      if (p.isReady) return state;
      const die = p.dicePool.find((d: Die) => d.id === action.dieId);
      if (!die || p.exhaustedDice.includes(action.dieId)) return state;
      const hasImproved = p.modifications.includes('improved_rollers');
      const cost = hasImproved ? 1 : 2;
      if (p.freePips < cost) return state;
      const old = die.value;
      let val: number;
      [s.rng, val] = rollDie(s.rng, die.size as DieSize);
      die.value = val;
      p.freePips -= cost;
      s.gameLog = log(s, `${p.name} rerolled d${die.size} from ${old} to ${val} (-${cost} pips)`);
      break;
    }

    case 'score_straight': {
      if (p.isReady) return state;
      const dice = action.diceIds.map((id: string) => p.dicePool.find((d: Die) => d.id === id)).filter(Boolean) as Die[];
      if (dice.length < 3) return state;
      const vals = dice.map(d => d.value).sort((a, b) => a - b);
      const hasVI = p.modifications.includes('vertical_integration');
      const isConsecutive = vals.every((v, i) => i === 0 || v === vals[i-1] + 1 || (hasVI && v === vals[i-1] + 2 && i === vals.length - 1));
      if (!isConsecutive) return state;
      const highest = Math.max(...vals);
      const hasSynergy = p.modifications.includes('synergy');
      let points = highest * (dice.length + (hasSynergy ? 1 : 0));
      if (!s.firstStraight) { points *= 2; s.firstStraight = true; }
      const hasPatent = p.modifications.includes('patent_protection');
      const idsToRemove = hasPatent
        ? action.diceIds.filter((id: string) => id !== dice.reduce((a, b) => a.value > b.value ? a : b).id)
        : action.diceIds;
      p.dicePool = p.dicePool.filter((d: Die) => !idsToRemove.includes(d.id));
      p.score += points;
      s.gameLog = log(s, `${p.name} scored straight: ${points} pts (total: ${p.score})`);
      break;
    }

    case 'score_set': {
      if (p.isReady) return state;
      const dice = action.diceIds.map((id: string) => p.dicePool.find((d: Die) => d.id === id)).filter(Boolean) as Die[];
      if (dice.length < 3) return state;
      const hasJV = p.modifications.includes('joint_venture');
      const uniqueVals = new Set(dice.map(d => d.value));
      if (!hasJV && uniqueVals.size !== 1) return state;
      if (hasJV && uniqueVals.size > 2) return state;
      const setVal = Math.max(...dice.map(d => d.value));
      const hasSynergy = p.modifications.includes('synergy');
      let points = setVal * (dice.length + 1 + (hasSynergy ? 1 : 0));
      if (!s.firstSet) { points *= 2; s.firstSet = true; }
      const hasPatent = p.modifications.includes('patent_protection');
      const idsToRemove = hasPatent
        ? action.diceIds.filter((id: string) => id !== dice.reduce((a, b) => a.value > b.value ? a : b).id)
        : action.diceIds;
      p.dicePool = p.dicePool.filter((d: Die) => !idsToRemove.includes(d.id));
      p.score += points;
      s.gameLog = log(s, `${p.name} scored set: ${points} pts (total: ${p.score})`);
      break;
    }

    case 'place_bid': {
      if (p.isReady) return state;
      const modCard = s.currentTurnModifications.find((m: ModBidCard) => m.id === action.modificationId);
      if (!modCard) return state;
      if (action.bidAmount < 0 || p.freePips < action.bidAmount) return state;
      const hasMarket = p.modifications.includes('market_manipulation');
      const minBid = hasMarket ? 0 : 0;
      if (action.bidAmount < minBid) return state;
      modCard.bids = modCard.bids.filter((b: any) => b.playerId !== action.playerId);
      modCard.bids.push({ playerId: p.id, playerName: p.name, amount: action.bidAmount });
      s.gameLog = log(s, `${p.name} placed bid of ${action.bidAmount} pips on ${FACTORY_MODIFICATIONS.find(m => m.id === modCard.modTypeId)?.name ?? modCard.modTypeId}`);
      break;
    }

    case 'end_turn': {
      if (p.isReady) return state;
      p.isReady = true;
      s.gameLog = log(s, `${p.name} ended their turn`);

      // Check if all players ready
      const allReady = s.players.every((pl: DFPlayerV1) => pl.isReady);
      if (allReady) {
        return resolveV1Turn(s);
      }
      break;
    }

    default:
      return state;
  }

  return s;
}

function resolveV1Turn(state: DFStateV1): DFStateV1 {
  let s = clone(state);

  // Resolve auctions
  for (const modCard of s.currentTurnModifications) {
    if (modCard.bids.length === 0) continue;
    const sorted = [...modCard.bids].sort((a: any, b: any) => b.amount - a.amount);
    const winner = sorted[0];
    modCard.winner = winner.playerId;
    const p = s.players.find((pl: DFPlayerV1) => pl.id === winner.playerId);
    if (p) {
      p.freePips -= winner.amount;
      p.modifications.push(modCard.modTypeId);
      const modDef = FACTORY_MODIFICATIONS.find(m => m.id === modCard.modTypeId);
      s.gameLog = log(s, `${p.name} won ${modDef?.name ?? modCard.modTypeId} for ${winner.amount} pips`);
    }
  }

  // Check end of game
  if (s.round >= s.maxRounds) {
    s.phase = 'complete';
    const best = s.players.reduce((a: DFPlayerV1, b: DFPlayerV1) => b.score > a.score ? b : a);
    s.winner = best.name;
    s.gameLog = log(s, `🏆 GAME OVER! ${best.name} wins with ${best.score} pts!`);
    return s;
  }

  // Advance round
  s.round++;
  let rng = s.rng;

  // Roll all dice, reset players
  s.players = s.players.map((p: DFPlayerV1) => {
    let pool = [...p.dicePool];
    pool = pool.map(d => {
      let val: number;
      [rng, val] = rollDie(rng, d.size as DieSize);
      return { ...d, value: val };
    });
    // Enforce dice floor
    while (pool.length < p.diceFloor) {
      let val: number;
      [rng, val] = rollDie(rng, 4);
      pool.push({ id: `die_${s.nextDieId++}`, size: 4, value: val });
    }
    return { ...p, dicePool: pool, exhaustedDice: [], isReady: false };
  });
  s.rng = rng;

  // Draw new turn modifications
  let modCards: ModBidCard[];
  let counter = (s.currentTurnModifications[s.currentTurnModifications.length - 1]
    ? parseInt(s.currentTurnModifications[s.currentTurnModifications.length - 1].id.split('_')[1]) + 1
    : 1);
  [modCards, s.modificationDeck, , counter] = drawTurnModifications(s.modificationDeck, s.rng, counter);
  s.currentTurnModifications = modCards;

  s.gameLog = log(s, `=== ROUND ${s.round} ===`);
  return s;
}

// ===== PUBLIC API =====

export function createGame(config: DFConfig, players: { id: string; name: string }[], seed?: number): DFState {
  const s = seed ?? Math.floor(Math.random() * 0xFFFFFFFF);
  if (config.version === 'v0.3') return createGameV3(players, s);
  if (config.version === 'v0.1.5') return createGameV1(config, players, s);
  return createGameV2(config, players, s);
}

export function applyAction(state: DFState, action: DFAction): DFState {
  if (state.version === 'v0.3') return applyActionV3(state as DFStateV3, action as DFActionV3);
  if (state.version === 'v0.1.5') return applyActionV1(state as DFStateV1, action as DFActionV1);
  return applyActionV2(state as DFStateV2, action as DFActionV2);
}

export function getPlayerView(state: DFState, playerId: string | null): DFPlayerView {
  if (state.version === 'v0.3') return getPlayerViewV3(state as DFStateV3);
  if (state.version === 'v0.1.5') {
    const s = state as DFStateV1;
    const p = s.players.find(pl => pl.id === playerId) ?? s.players[0];
    return {
      ...s,
      playerId: p?.id ?? '',
      playerName: p?.name ?? '',
      myDicePool: p?.dicePool ?? [],
      myExhaustedDice: p?.exhaustedDice ?? [],
      myFreePips: p?.freePips ?? 0,
      myScore: p?.score ?? 0,
      myEffects: p?.effects ?? [],
      myModifications: p?.modifications ?? [],
    } as DFPlayerViewV1;
  }

  const s = state as DFStateV2;
  const p = s.players.find(pl => pl.id === playerId) ?? s.players[0];
  return {
    ...s,
    playerId: p?.id ?? '',
    playerName: p?.name ?? '',
    myDicePool: p?.dicePool ?? [],
    myExhaustedDice: p?.exhaustedDice ?? [],
    mySlots: p?.slots ?? {},
    myCards: p?.cards ?? [],
    myExhaustedCards: p?.exhaustedCards ?? [],
    myVictoryPoints: p?.victoryPoints ?? 0,
    recruitCost: p ? getRecruitCost(p) : 1,
    targetedRecruitCost: p ? getTargetedRecruitCost(p) : 1,
    recruitPlusCost: p ? getRecruitPlusCost(p) : 1,
  } as DFPlayerViewV2;
}

export function describe(config: DFConfig): string {
  if (config.version === 'v0.3') return describeV3();
  if (config.version === 'v0.1.5') {
    return [
      `Dice Factory v0.1.5 — ${config.maxRounds ?? 11} rounds. Highest score wins.`,
      '',
      'Each player manages a dice pool (d4–d12). On your turn, use factory actions to improve your dice, then score:',
      '  Recruit — add a new d4 to your pool.',
      '  Promote — upgrade a die to the next size (d4→d6→d8→d10→d12).',
      '  Process — lock in dice values as free pips to spend later.',
      '  Reroll — reroll any die in your pool.',
      '  Modify — nudge a die value ±1.',
      '  Score Straight — submit dice with consecutive values for points.',
      '  Score Set — submit dice with matching values for points.',
      'Factory Modifications: each round, bid dice-pips on modification cards that permanently upgrade your actions.',
      'End turn when done. Round ends when all players pass.',
    ].join('\n');
  }

  const tiers = config.abilityTiers ?? [1, 2, 3, 4];
  return [
    `Dice Factory v0.2.1 — First to 10 VP wins. Ability tiers in play: 0${tiers.map(t => `, ${t}`).join('')}.`,
    '',
    'Each player has a dice pool (starting: 4×d4) and 12 ability slots.',
    'Slots 1–4: tier-0 (always available): Recruit, Reroll, Bump, Promote.',
    'Slots 5–12: unlockable higher-tier abilities (tiers 1–4) assigned from the shared ability market.',
    '',
    'On your turn you have 2 actions. Each action: activate one slot ability, buy a factory card, or pass.',
    '  Recruit — add a new die to your pool (cost increases with pool size).',
    '  Reroll — reroll a die in your pool.',
    '  Bump — adjust a die value up or down by 1.',
    '  Promote — upgrade a die to the next size (d4→d6→d8→d10→d12); it rolls immediately.',
    '  Higher-tier abilities — more powerful effects (draw cards, attack opponents, etc.).',
    '  Buy card — spend dice from your pool to purchase a factory card; cards grant passive bonuses and VP.',
    '  Pass turn — skip remaining actions; you are skipped for the rest of this round.',
    '',
    'All dice re-roll at the start of each new round. Exhausted dice and cards refresh.',
    'Some abilities let you attack opponents; targets must respond by sacrificing dice.',
    'First player to reach 10 VP wins.',
  ].join('\n');
}

export function getValidActions(state: DFState, playerId: string): DFAction[] {
  if (state.version === 'v0.3') return [{ type: 'v3_roll_all', playerId }];
  if (state.version === 'v0.1.5') {
    const s = state as DFStateV1;
    const p = s.players.find(pl => pl.id === playerId);
    if (!p || p.isReady) return [];
    return [{ type: 'end_turn', playerId }];
  }

  const s = state as DFStateV2;
  if (s.phase === 'ended') return [];
  const cp = s.players[s.currentPlayerIndex];
  if (!cp || cp.id !== playerId) {
    // Can still respond to attacks
    const attacks = s.pendingAttacks.filter(a => a.waitingFor.includes(playerId));
    if (attacks.length > 0) {
      return [{ type: 'respond_to_attack', playerId, attackId: attacks[0].id, diceIds: [] }];
    }
    return [];
  }
  return [{ type: 'pass_turn', playerId }];
}

export function getBotAction(player: { id: string }, state: DFState): DFAction | null {
  if (state.version === 'v0.3') return null;
  if (state.version === 'v0.1.5') {
    const s = state as DFStateV1;
    const p = s.players.find(pl => pl.id === player.id);
    if (!p || p.isReady) return null;
    return { type: 'end_turn', playerId: player.id };
  }

  const s = state as DFStateV2;
  const cp = s.players[s.currentPlayerIndex];
  if (!cp || cp.id !== player.id) return null;
  return { type: 'pass_turn', playerId: player.id };
}
